import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import useAuthStore from '../../stores/authStore';
import { useServerHealth } from '../../hooks/useServerHealth';
import { AuthPageLayout, AuthForm } from '../../components/layout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { validateEmail } from '../../utils/validation';
import { LoginRequest } from '../../services/auth';
import { parseInviteLinkFromSearch } from '../../utils/inviteLink';
import { pageStyles } from '../../config/theme';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const { health, backendUrl, imageServerUrl } = useServerHealth();
  
  // Get current hostname and protocol for health endpoint links
  // This ensures links work when accessing from phone/network IP
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  
  // Extract ports from URLs for health endpoint links
  const getBackendPort = (url: string): string => {
    const match = url.match(/:(\d+)/);
    return match?.[1] ?? '3031';
  };
  
  const getImageServerPort = (url: string): string => {
    const match = url.match(/:(\d+)/);
    return match?.[1] ?? '3032';
  };
  
  const backendHealthUrl = `${currentProtocol}//${currentHost}:${getBackendPort(backendUrl)}/health`;
  const imageServerHealthUrl = `${currentProtocol}//${currentHost}:${getImageServerPort(imageServerUrl)}/health`;
  
  const [formData, setFormData] = useState<LoginRequest>(() => {
    const savedFormData = sessionStorage.getItem('loginFormData');
    if (savedFormData) {
      try {
        const parsed = JSON.parse(savedFormData);
        return parsed;
      } catch (error) {
        console.warn('Failed to parse saved login form data:', error);
      }
    }
    return {
      email: '',
      password: '',
    };
  });
  
  const [formErrors, setFormErrors] = useState<Partial<LoginRequest>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userTypingRef = useRef(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      // Clear sessionStorage before redirecting
      sessionStorage.removeItem('loginFormData');
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors when form data changes (but preserve form data)
  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      setFormErrors({});
    }
  }, [formData]);

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('loginFormData', JSON.stringify(formData));
  }, [formData]);

  // Clear server errors when user starts typing (but not during submission)
  useEffect(() => {
    if (error && !isSubmitting && userTypingRef.current) {
      clearError();
    }
    // Reset the typing flag after clearing error
    if (userTypingRef.current) {
      userTypingRef.current = false;
    }
  }, [formData.email, formData.password, error, clearError, isSubmitting]);

  // When we have a returnUrl (e.g. sent here after backend was down), periodically retry
  // restoreSession so that when the backend comes back we can restore auth and redirect.
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');
  const RESTORE_RETRY_INTERVAL_MS = 10000; // 10 seconds

  useEffect(() => {
    if (!returnUrl || isAuthenticated) return;

    const tryRestore = () => {
      useAuthStore.getState().restoreSession().catch(() => {
        // Ignore; we stay on login. Next interval will retry.
      });
    };

    const intervalId = setInterval(tryRestore, RESTORE_RETRY_INTERVAL_MS);
    // Also try once after a short delay (in case backend just came back)
    const timeoutId = setTimeout(tryRestore, 2000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [returnUrl, isAuthenticated]);

  // Clear sessionStorage when user navigates to a different page (not during login)
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('loginFormData');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const validateForm = (): boolean => {
    const errors: Partial<LoginRequest> = {};

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Mark that user is typing
    userTypingRef.current = true;
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Extract invite vars from returnUrl when present (e.g. from ProtectedRoute redirect)
    let inviteVariables: Record<string, string> | undefined;
    const returnUrl = new URLSearchParams(location.search).get('returnUrl');
    if (returnUrl) {
      try {
        const search = returnUrl.includes('?') ? returnUrl.slice(returnUrl.indexOf('?')) : '';
        const { linkVariables } = parseInviteLinkFromSearch(search);
        if (linkVariables && Object.keys(linkVariables).length > 0) {
          inviteVariables = linkVariables;
        }
      } catch {
        // Ignore parse errors
      }
    }

    const loginPayload: LoginRequest = { ...formData };
    if (inviteVariables) {
      loginPayload.inviteVariables = inviteVariables;
    }
    
    try {
      await login(loginPayload);
      // Navigation will be handled by useEffect when isAuthenticated changes
      // Clear saved form data on successful login
      sessionStorage.removeItem('loginFormData');
    } catch (error) {
      // Error is handled by the useAuth hook and displayed via error state
      // Form data is preserved - no need to clear it
      console.error('Login failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  // Format last checked time
  const formatLastChecked = (date: Date | null): string => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  };

  return (
    <AuthPageLayout
      title="Storynity"
      subtitle=""
    >
      <AuthForm onSubmit={handleSubmit}>
        <div className="px-6 py-4">
          <div className="mb-6">
            <p style={{ ...pageStyles.subtitle, fontStyle: 'italic' }}>
              Sorry, it seems we are having technical issues...
            </p>
            {returnUrl && (
              <p style={{ ...pageStyles.subtitle, fontSize: '0.875rem', marginTop: '0.5rem', opacity: 0.9 }}>
                We&apos;ll try to restore your session automatically when the server is back. You can also sign in below.
              </p>
            )}
          </div>

          {/* Backend Server Status */}
          <div className="mb-6">
            <div
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 style={{ color: '#ffffff', fontSize: '1.125rem', fontWeight: '600' }}>
                  Backend Server
                </h3>
                <span
                  style={{
                    color: health.backend.healthy ? '#10b981' : '#ef4444',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {health.backend.healthy ? '✓ Online' : '✗ Offline'}
                </span>
              </div>
              
              {health.backend.error && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {health.backend.error}
                </p>
              )}
              
              {health.backend.lastChecked && (
                <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  Last checked: {formatLastChecked(health.backend.lastChecked)}
                </p>
              )}
              
              <a
                href={backendHealthUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#60a5fa',
                  fontSize: '0.875rem',
                  textDecoration: 'underline',
                  marginTop: '0.5rem',
                  display: 'inline-block'
                }}
              >
                View health endpoint →
              </a>
            </div>
          </div>

          {/* Image Server Status */}
          <div className="mb-6">
            <div
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 style={{ color: '#ffffff', fontSize: '1.125rem', fontWeight: '600' }}>
                  Image Server
                </h3>
                <span
                  style={{
                    color: health.imageServer.healthy ? '#10b981' : '#ef4444',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {health.imageServer.healthy ? '✓ Online' : '✗ Offline'}
                </span>
              </div>
              
              {health.imageServer.error && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {health.imageServer.error}
                </p>
              )}
              
              {health.imageServer.lastChecked && (
                <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  Last checked: {formatLastChecked(health.imageServer.lastChecked)}
                </p>
              )}
              
              <a
                href={imageServerHealthUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#60a5fa',
                  fontSize: '0.875rem',
                  textDecoration: 'underline',
                  marginTop: '0.5rem',
                  display: 'inline-block'
                }}
              >
                View health endpoint →
              </a>
            </div>
          </div>
        </div>

        {/* Email Input */}
        <Input
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          {...(formErrors.email && { error: formErrors.email })}
          placeholder="Enter your email"
          fullWidth
          autoComplete="email"
          autoFocus
          disabled={isSubmitting || isLoading}
          className="bg-white/90"
          labelStyle={{ color: '#ffffff' }}
        />

        {/* Password Input */}
        <Input
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          {...(formErrors.password && { error: formErrors.password })}
          placeholder="Enter your password"
          fullWidth
          autoComplete="current-password"
          disabled={isSubmitting || isLoading}
          className="bg-white/90"
          labelStyle={{ color: '#ffffff' }}
        />

        {/* Error Display */}
        {error && (
          <div 
            style={{
              backgroundColor: '#fed7aa',
              border: '1px solid #fb923c',
              borderRadius: '0.375rem',
              padding: '1rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem' }}>
              <svg 
                style={{ height: '1.25rem', width: '1.25rem', color: '#dc2626', marginRight: '0.25rem', flexShrink: 0 }}
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '700', color: '#dc2626', margin: 0 }}>
                Login Failed
              </h3>
            </div>
            <div style={{ fontSize: '0.875rem', color: '#dc2626', textAlign: 'left' }}>
              <p style={{ margin: 0 }}>{error.message}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="glass-primary"
          size="lg"
          fullWidth
          loading={isSubmitting || isLoading}
          disabled={
            isSubmitting || 
            isLoading || 
            !health.backend.healthy || 
            !health.imageServer.healthy ||
            !health.backend.lastChecked ||
            !health.imageServer.lastChecked
          }
        >
          {isSubmitting || isLoading ? 'Signing In...' : 'Sign In'}
        </Button>

        {/* Links */}
        <div className="text-center space-y-2">
          <Link
            to="/auth/reset-password"
            style={pageStyles.link.primary}
            className="text-sm font-medium"
          >
            Forgot your password?
          </Link>
        </div>
      </AuthForm>
    </AuthPageLayout>
  );
};

export default LoginPage;
